import {useCallback, useContext, useEffect, useMemo, useRef, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useDebouncedCallback} from 'use-debounce';
import {WORKSPACE_SIZE_DEFAULT} from '../constants';
import {SlideRefContext} from '../context/SlideRefContext';
import {toggleRedrawAction} from '../store/actions/dashboard.actions';
import {updateScale} from '../store/actions/presentation.actions';
import {currentSlideIndexSelector, loadedPresentationSelector} from '../store/selectors/presentation.selectors';

function usePresScale(): {
    scale: number;
    slideSize: {
        width: number;
        height: number;
    };
    handleResizeDebounced: () => void;
    slideRect: DOMRect | null;
    transformPointFromFraction: (point: {x: number; y: number}) => {x: number; y: number};
    transformPointToFraction: (point: {x: number; y: number}) => {x: number; y: number};
} {
    const dispatch = useDispatch();

    const loadedPresentation = useSelector(loadedPresentationSelector);
    const selectedSlideIndex = useSelector(currentSlideIndexSelector);
    const {slideWrapperRef, slideRef} = useContext(SlideRefContext);

    const [slideSize, setSlideSize] = useState(WORKSPACE_SIZE_DEFAULT);
    const [slideRect, setSlideRect] = useState<DOMRect | null>(null);
    const resizeObserverRef = useRef<ResizeObserver | null>(null);

    const handleSlideResize = useDebouncedCallback(() => {
        const rect = slideRef.current?.getBoundingClientRect() || null;
        setSlideRect(rect);
    }, 300);

    useEffect(() => {
        handleSlideResize();

        window.addEventListener('resize', handleSlideResize);

        return () => {
            window.removeEventListener('resize', handleSlideResize);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [slideRef.current]);

    const currentSlide = useMemo(() => {
        return loadedPresentation?.slides[selectedSlideIndex];
    }, [loadedPresentation, selectedSlideIndex]);

    const getWorkspaceSize = useCallback(() => {
        const currentSlideSize = currentSlide?.workspaceSize;
        const loadedPresentationSize = loadedPresentation?.workspaceSize;

        const width = currentSlideSize?.width || loadedPresentationSize?.width || WORKSPACE_SIZE_DEFAULT.width;
        const height = currentSlideSize?.height || loadedPresentationSize?.height || WORKSPACE_SIZE_DEFAULT.height;

        return {width, height};
    }, [loadedPresentation, currentSlide]);

    const getSlideSizeRatio = useCallback((): number => {
        const {width, height} = getWorkspaceSize();
        return width / height;
    }, [getWorkspaceSize]);

    // slide width / height ratio
    const handleResize = useCallback((): void => {
        const slideSizeRatio = getSlideSizeRatio();

        const slideContainer = slideWrapperRef.current;
        if (slideContainer) {
            const {width, height} = slideContainer.getBoundingClientRect();
            let newWidth = width;
            let newHeight = height;

            if (slideSizeRatio > 1) {
                // width > heighta
                newHeight = width / slideSizeRatio;

                if (newHeight > height) {
                    newHeight = height;
                    newWidth = height * slideSizeRatio;
                }
            } else if (slideSizeRatio < 1) {
                // height > widtha
                newWidth = height * slideSizeRatio;

                if (newWidth > width) {
                    newWidth = width;
                    newHeight = width / slideSizeRatio;
                }
            }

            // set new slide width height
            setSlideSize({width: newWidth, height: newHeight});
        }
    }, [slideWrapperRef, getSlideSizeRatio]);

    const handleResizeDebounced = useDebouncedCallback(() => {
        dispatch(toggleRedrawAction());
        handleResize();
    }, 300);

    useEffect(() => {
        if (slideWrapperRef.current) {
            resizeObserverRef.current = new ResizeObserver(() => {
                handleResizeDebounced();
            });

            resizeObserverRef.current.observe(slideWrapperRef.current);
        }

        return () => {
            if (resizeObserverRef.current) {
                resizeObserverRef.current.disconnect();
            }
        };
    }, [handleResizeDebounced, slideWrapperRef]);

    const scale = useMemo(() => {
        return slideSize.width / getWorkspaceSize().width;
    }, [slideSize.width, getWorkspaceSize]);

    useEffect(() => {
        dispatch(updateScale(scale));
    }, [dispatch, scale]);

    const transformPointFromFraction = useCallback(
        (point: {x: number; y: number}): {x: number; y: number} => {
            if (!slideRect?.width || !slideRect?.height) return point;
            return {
                x: point.x * slideRect.width,
                y: point.y * slideRect.height,
            };
        },
        [slideRect?.width, slideRect?.height]
    );

    const transformPointToFraction = useCallback(
        (point: {x: number; y: number}): {x: number; y: number} => {
            if (!slideRect?.width || !slideRect.height) return point;
            return {
                x: point.x / slideRect.width,
                y: point.y / slideRect.height,
            };
        },
        [slideRect?.width, slideRect?.height]
    );

    return {
        scale: slideSize.width / getWorkspaceSize().width,
        slideSize,
        handleResizeDebounced,
        slideRect,
        transformPointFromFraction,
        transformPointToFraction,
    };
}

export default usePresScale;
